import {
  useProductOptions,
  AddToCartButton,
  BuyNowButton,
} from '@shopify/hydrogen';
import {
  BUTTON_PRIMARY_CLASSES,
  BUTTON_SECONDARY_CLASSES,
} from '../Button.client';
import {isSelectedVariantAvailable} from '../../utils/product';
import {RecordClickEvent} from '../Analytics/RecordClickEvent.client';
import {MonorailEvent, timedTrackEvent} from '../../utils/monorail.client';
import {ShoppingBagEvent} from '../../utils/shoppingBagAnalytics';
import {usePageContext} from '../../contexts/PageContext.client';
import {useRef, useState} from 'react';
import {useErrorLogger} from '@shopify/react-bugsnag';
import classNames from 'classnames';
import {usePublicPageI18n} from '../../contexts/PublicPageI18n.client';
import {base64Decode} from 'shared/utils/base64';

export function AddToCartMarkup({
  quantity,
  closeModal,
  product,
}: {
  product: unknown;
  quantity: number;
  closeModal: () => void;
}) {
  const {t} = usePublicPageI18n();

  const buyNowButtonWrapper = useRef<HTMLDivElement>(null);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  const logger = useErrorLogger();

  const {selectedVariant, selectedOptions} = useProductOptions();

  // @ts-expect-error TODO: fix typing for Product
  const {id: productId, title: productName} = product;

  const pageCtx = usePageContext();

  const available =
    selectedVariant &&
    selectedOptions &&
    isSelectedVariantAvailable(
      selectedVariant.selectedOptions,
      selectedOptions,
    );

  const isAvailable =
    available && selectedVariant.availableForSale && selectedVariant.id;

  return (
    <div className={classNames('mb-8', 'space-y-2')}>
      {pageCtx.slug !== 'linkpoptruck' && (
        <RecordClickEvent
          event={MonorailEvent.TrackShoppingBag}
          eventType={ShoppingBagEvent.ClickAddToBag}
          data={{
            pageId: pageCtx.id,
            currency: selectedVariant?.priceV2?.currencyCode,
            products: [
              {
                productVariantId: base64Decode(selectedVariant?.id || ''),
                productId,
                productName,
                productPrice: selectedVariant?.priceV2?.amount,
                productQuantity: quantity,
              },
            ],
          }}
        >
          <a onClick={closeModal}>
            <AddToCartButton
              variantId={base64Decode(selectedVariant?.id || '')}
              data-testid="add-to-cart"
              className={BUTTON_PRIMARY_CLASSES}
              disabled={!isAvailable}
              quantity={quantity}
            >
              {isAvailable ? t('addToBag') : t('unavailable')}
            </AddToCartButton>
          </a>
        </RecordClickEvent>
      )}
      {/*
      TODO: Revert the "Buy it now" button back to this when beacon tracking is
            implemented.
      */}
      {/*isAvailable && (
        <Product.SelectedVariant.BuyNowButton
          className={BUTTON_SECONDARY_CLASSES}
        >
          Buy it now
        </Product.SelectedVariant.BuyNowButton>
      )*/}

      {/*
      FIXME: This is a hack to be able to track the "Buy it now" button.

             Instead of showing the Hydrogen BuyNowButton, we show a regular
             button. We also render a hidden BuyNowButton, wrapped in a div with
             a ref attached.

             When the regular button is clicked, it calls `trackEvent` and waits
             for it to complete, and then triggers a click on the BuyNowButton
             via the `buyNowButtonWrapper` ref.
      */}
      {isAvailable && (
        <>
          <div ref={buyNowButtonWrapper} style={{display: 'none'}}>
            <BuyNowButton
              quantity={quantity}
              variantId={selectedVariant.id || ''}
            >
              {t('buyItNow')}
            </BuyNowButton>
          </div>

          <button
            className={BUTTON_SECONDARY_CLASSES}
            disabled={loadingCheckout}
            onClick={async () => {
              setLoadingCheckout(true);

              try {
                await timedTrackEvent({
                  event: MonorailEvent.TrackShoppingBag,
                  eventType: ShoppingBagEvent.ClickBuyNow,
                  data: {
                    pageId: pageCtx.id,
                    currency: selectedVariant.priceV2?.currencyCode,
                    products: [
                      {
                        productVariantId: selectedVariant.id || '',
                        productId,
                        productName,
                        productPrice: selectedVariant.priceV2?.amount || '',
                        productQuantity: quantity,
                      },
                    ],
                  },
                });
              } catch (error) {
                logger.notify(
                  new Error(
                    `event tracking error: ${MonorailEvent.TrackShoppingBag}: ${error}`,
                  ),
                );
              }

              const buyNowButton = buyNowButtonWrapper.current?.children[0];
              buyNowButton instanceof HTMLElement && buyNowButton.click();
            }}
          >
            {t('buyItNow')}
          </button>
        </>
      )}
    </div>
  );
}
